import { OmitUnion } from "@redotech/util/type";
import { DataType, DataTypeName } from "./data-types";
import { AdvancedFlowTokenType as AdvancedFlowConditionTokenType } from "./data-types/base-types";
import {
  GenericDataType,
  GenericPrefixes,
  GenericsOf,
  removeGenericFromType,
} from "./data-types/generic-types";

/**
 * Infers the true type of a schema's field by recursively unwrapping generic prefixes.
 */
type Value<T> = T extends `${GenericPrefixes["Array"]}${infer U}`
  ? Value<U>[]
  : T extends `${GenericPrefixes["Optional"]}${infer V}`
    ? Value<V> | undefined
    : T extends DataTypeName
      ? DataType[T]
      : never;

export interface SchemaFields {
  [key: string]: OmitUnion<SchemaField, "key">;
}

/** The types carrying data of their own, so narrowing to one gains something. */
type FieldDataType = "Enum" | "Token" | "Text";

/**
 * Narrows a field to the shape its underlying type carries, so a reader gets
 * `values`/`tokenType` however the field's type is decorated.
 */
export function isFieldOfType<T extends FieldDataType>(
  field: SchemaFields[string],
  baseType: T,
): field is Extract<SchemaFields[string], { dataType: GenericsOf<T> }> {
  return removeGenericFromType(field.dataType) === baseType;
}

/**
 * The generic type for datatypes that can be passed as input to an email flow.
 *
 * @example
 * const customerEventSchema = defineSchema({
 *  fields: {
 *    email: {
 *    dataType: "Email",
 *    documentation: "The email address of the customer who triggered the event",
 *  },
 * });
 */
export interface Schema {
  fields: SchemaFields;
}

export type SchemaField = {
  key: string;
  /** Display label override — shown instead of the auto-derived camelToTitleCase(key). */
  label?: string;
  documentation: string;
  prettyValues?: Record<string, string>;
  /**
   * If true, hides the field from the "insert variable" modal in the email/SMS builders.
   * Typically only needs to be set for textlike data types.
   */
  hideFromInsertionModal?: boolean;
  /**
   * If true, hides the field from the condition step in the advanced flow builder.
   */
  hideFromConditionStep?: boolean;
  /**
   * Optional predicate function to conditionally include this field based on enabled integrations.
   * If undefined, the field is always included.
   * If defined, the field is only included when the predicate returns true.
   */
  shouldInclude?: (enabledIntegrations: Record<string, boolean>) => boolean;
} & (
  | {
      dataType: Exclude<
        GenericDataType,
        GenericsOf<"Enum" | "Token" | "Text" | "Cart Context">
      >;
      values?: string[];
    }
  | { dataType: GenericsOf<"Enum">; values: string[] }
  | { dataType: GenericsOf<"Token">; tokenType: AdvancedFlowConditionTokenType }
  /**
   * "Cart Context" offers cart branching in the editor, so a field must hide
   * it or deliberately label it. The last branch only shapes the compile error.
   */
  | ({ dataType: GenericsOf<"Cart Context"> } & (
      | { hideFromConditionStep: true }
      | { label: "Cart condition" }
      | {
          "Declare Cart Context fields via recommendationCartContextFields or branchableCartContextFields in common-fields.ts": true;
        }
    ))
  | {
      dataType: GenericsOf<"Text">;
      /**
       * Identifier for the type of suggestions to fetch.
       * The UI component will map this to the appropriate RPC call.
       */
      suggestionType?: "orderSource";
    }
);
/**
 * A helper function to define a schema that provides increased type safety.
 * Without using it, the type of the schema is inferred broadly,
 * thus making it difficult to hardcode example schemas.
 */
export function defineSchema<T extends Schema>(schema: T) {
  return schema;
}

/**
 * An instance of a Schema.
 *
 * @example
 * const customerEvent<SchemaInstance<typeof customerEventSchema>> = {
 *  fields: {
 *    email: "john@fake.com"
 *  }
 * };
 */
export type SchemaInstance<T extends Schema> = {
  [K in keyof T["fields"]]: Value<T["fields"][K]["dataType"]>;
};
